import {
    Palette_AccessibleDarkMode,
    Palette_AccessibleLightMode,
    Palette_DarkMode,
    Palette_LightMode,
} from './PaletteVariations';
import type { PaletteInterface, PaletteName } from './types';

const getCSSVariableNameFromColorAndRank = (color: string, rank: string): string =>
    `--kalshi-palette-${color.toLowerCase()}-${rank.toLowerCase()}`;

const getCSSClassNameFromPaletteName = (palette: PaletteName): string | undefined =>
    !palette || palette === 'light'
        ? undefined
        : palette === 'dark'
          ? 'dark'
          : palette === 'accessible-light'
            ? 'accessible-light'
            : palette === 'accessible-dark'
              ? 'accessible-dark'
              : undefined;

/**
 * Updates site-wide color palette by setting new values for all Kalshi CSS propery keys.
 *
 * @param palette
 */
export const __directlySetCSSColorPaletteVariables = (palette: PaletteName): void => {
    let PaletteToUse = Palette_LightMode;
    if (palette === 'accessible-dark') PaletteToUse = Palette_AccessibleDarkMode;
    else if (palette === 'accessible-light') PaletteToUse = Palette_AccessibleLightMode;
    else if (palette === 'dark') PaletteToUse = Palette_DarkMode;
    else if (palette === 'light') PaletteToUse = Palette_LightMode;

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const className = getCSSClassNameFromPaletteName(palette);
        const classesToRemove = ['dark', 'accessible-light', 'accessible-dark'].filter((c) => c !== className);
        document.documentElement.classList.remove(...classesToRemove);
        if (!!className) {
            document.documentElement.classList.add(className);
        }

        for (const color in PaletteToUse) {
            for (const rank in (PaletteToUse as any)[color]) {
                document.documentElement.style.setProperty(
                    getCSSVariableNameFromColorAndRank(color, rank),

                    (PaletteToUse as any)[color][rank],
                );
            }
        }
    }
};

const getGlobalsCSSVariable = (color: string, rank: string): string => `--${color.toLowerCase()}-${rank.toLowerCase()}`;

const makeVariablePalette = (): PaletteInterface => {
    const variablePalette = {};
    for (const color in Palette_LightMode /* using the lightmode palette as a prototypical palette */) {
        (variablePalette as any)[color] = {};

        for (const rank in (Palette_LightMode as any)[color]) {
            const hardcoded = (Palette_LightMode as any)[color][rank];
            const kalshiVar = getCSSVariableNameFromColorAndRank(color, rank);
            const globalsVar = getGlobalsCSSVariable(color, rank);
            // Nested fallback: kalshi-palette var → globals.css var (respects dark class) → hardcoded light value

            (variablePalette as any)[color][rank] = `var(${kalshiVar}, var(${globalsVar}, ${hardcoded}))`;
        }
    }
    return variablePalette as PaletteInterface;
};

// The actual palette to be used across the site. User configurable.
/** @deprecated Use `Palette` from `@/DesignSystem3/Palette` instead. */
export const Palette_Variable = makeVariablePalette();

/**
 * Get the current stored value of a CSS color variable using JS.
 * This should only be used when passing a color to an external component which does not support CSS variables.
 */
export const getResolvedCSSVariableValueForPalette = (color: string, rank: string): string =>
    document.documentElement.style.getPropertyValue(getCSSVariableNameFromColorAndRank(color, rank)) ||
    (Palette_LightMode as any)[color][rank]; // Fallback to light palette if css variable is missing
